import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { isArray } from 'lodash';
import { v4 as uuid } from 'uuid';
import { getTipoModeloLabelByValue } from '../../shared-components/select-tipo-modelo/QCXFinalSelectTipoModeloAutocomplete';
import { isSubCreateMode } from '../../utils/store/store-utils';
import { IDLE_CONTROL } from '../config-control';
import {
  BACKGROUND_CREATE_MODE,
  CONSULT_MODE,
  CREATE_MODE,
  NONE_BACKGROUND_MODE,
  NONE_MODE,
  NONE_SUB_MODE,
  SUB_CONSULT_MODE,
  SUB_CREATE_MODE,
  SUB_UPDATE_MODE,
  UPDATE_MODE,
} from '../mode';
import {
  ALTERNATIVE_LOADING_STATUS,
  FAILURE_STATUS,
  IDLE_STATUS,
  LOADING_STATUS,
  SUCCESS_STATUS,
} from '../status';
import {
  activateById, fetchAll,
  fetchByFilter,
  fetchById, inactivateById,
} from './modeloNumerarioAPI';
import i18n from '../../i18n';

const initialState = {
  status: IDLE_STATUS,
  mode: {
    main: NONE_MODE,
    background: NONE_BACKGROUND_MODE,
    sub: NONE_SUB_MODE,
  },
  error: null,
  response: {
    status: null,
    message: null,
    data: null,
  },
  config: {
    control: IDLE_CONTROL,
  },
  list: [],
  model: {
    code: '',
    description: '',
    active: null,
    etapa: {
      id: '',
    },
    modelosLancamento: [],
    viaTransporte: {
      id: '',
    },
  },
  related: {
    model: {
      modeloLancamento: {},
    },
  },
  locked: {
    model: {
      modeloLancamento: {},
    },
  },
};

const fetchAllAsync = createAsyncThunk(
  'modeloNumerario/fetchAll',
  async () => {
    const { data } = await fetchAll();
    return { data };
  }
);

const fetchAllAsyncAndProcess = createAsyncThunk(
  'modeloNumerario/fetchAllAndProcess',
  async ({ exclude }) => {
    const { data } = await fetchAll();

    if (isArray(exclude)) {
      const processed = data.filter((model) => (
        exclude.indexOf(model?.id) === -1
      ));

      return {
        data: processed,
      };
    }

    return { data };
  }
);

const fetchByIdAsync = createAsyncThunk(
  'modeloNumerario/fetchById',
  async (data) => {
    const response = await fetchById(data);
    return {
      response: {
        status: response.status,
        data: response.data,
      },
    };
  }
);

const activateByIdAsync = createAsyncThunk(
  'modeloNumerario/activateById',
  async (id) => {
    const { status, data } = await activateById(id);
    return { response: { status, data } };
  }
);

const fetchByFilterAsync = createAsyncThunk(
  'modeloNumerario/fetchByFilter',
  async (params) => {
    const { data } = await fetchByFilter(params);
    return { data };
  }
);
const inactivateByIdAsync = createAsyncThunk(
  'modeloNumerario/inactivateById',
  async (id) => {
    const { status, data } = await inactivateById(id);
    return { response: { status, data } };
  }
);

const modeloNumerarioSlice = createSlice({
  name: 'modeloNumerario',
  initialState,
  reducers: {
    changeStatusTo: (state, action) => {
      state.status = action.payload.status;
    },
    loading: (state) => {
      state.status = LOADING_STATUS;
    },
    success: (state) => {
      state.status = SUCCESS_STATUS;
    },
    failure: (state) => {
      state.status = FAILURE_STATUS;
    },
    resetStatus: (state) => {
      state.status = IDLE_STATUS;
    },
    resetList: (state) => {
      state.list = [];
    },
    changeToCreateMode: (state) => {
      state.mode.main = CREATE_MODE;
    },
    changeToConsultMode: (state) => {
      state.mode.main = CONSULT_MODE;
    },
    changeToUpdateMode: (state) => {
      state.mode.main = UPDATE_MODE;
    },
    resetMode: (state) => {
      state.mode.main = NONE_MODE;
    },
    changeToBackgroundCreateMode: (state) => {
      state.mode.background = BACKGROUND_CREATE_MODE;
    },
    resetBackgroundMode: (state) => {
      state.mode.background = NONE_BACKGROUND_MODE;
    },
    changeToSubCreateMode: (state) => {
      state.mode.sub = SUB_CREATE_MODE;
      state.related.model.modeloLancamento = {};
    },
    changeToSubConsultMode: (state) => {
      state.mode.sub = SUB_CONSULT_MODE;
    },
    changeToSubUpdateMode: (state) => {
      state.mode.sub = SUB_UPDATE_MODE;
    },
    resetSubMode: (state) => {
      state.mode.sub = NONE_SUB_MODE;
    },
    changeControlTo: (state, action) => {
      state.config.control = action.payload;
    },
    setModel: (state, action) => {
      const updatedModelosLancamentoList = action.payload.modelosLancamento.map(
        (current) => ((current?.id === null)
          ? {
            ...current,
            id: uuid(),
          }
          : current
        )
      );

      state.model = {
        ...action.payload,
        modelosLancamento: updatedModelosLancamentoList,
      };
    },
    addToList: (state, action) => {
      state.list = [...state.list, action.payload.data];
    },
    updateOnList: (state, action) => {
      state.list = state.list.map((current) => {
        if (current.id === action.payload.data?.id) {
          return action.payload.data;
        }
        return current;
      });
    },

    setRelatedModeloLancamentoModel: (state, action) => {
      state.related.model.modeloLancamento = {
        ...action.payload,
      };
      state.mode.sub = SUB_CONSULT_MODE;
    },
    lockSelectedModeloLancamento: (state) => {
      state.locked.model.modeloLancamento = state.related.model.modeloLancamento;
    },
    resetLockedModeloLancamentoModel: (state) => {
      state.locked.model.modeloLancamento = {};
    },
    addModeloLancamento: (state, action) => {
      state.status = LOADING_STATUS;

      const newModeloLancamento = action.payload.data;

      const alreadyExists = state.model.modelosLancamento
        .some((existingModeloLancamento) => {
          const relatedDespesaReceitaIdFromExisting = existingModeloLancamento?.despesaReceita?.id;
          const relatedDespesaReceitaIdFromNew = newModeloLancamento?.despesaReceita?.id;

          return relatedDespesaReceitaIdFromExisting === relatedDespesaReceitaIdFromNew;
        });

      if (alreadyExists) {
        const tipoModeloLabelOfNewModeloLancamento = getTipoModeloLabelByValue(
          newModeloLancamento?.tipo
        );

        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.mensagem.despesaAdicionadaModeloNumerario', { tipoModeloLancamento: tipoModeloLabelOfNewModeloLancamento, despesaReceita: newModeloLancamento?.despesaReceita?.id }),
        };
        return;
      }

      state.model.modelosLancamento = [
        ...state.model.modelosLancamento,
        {
          ...newModeloLancamento,
          id: uuid(),
        }
      ];
      state.related.model.modeloLancamento = {};
      state.mode.sub = NONE_SUB_MODE;
      state.status = IDLE_STATUS;
    },
    updateModeloLancamento: (state, action) => {
      const updated = action.payload.data;

      const alreadyExists = state.model.modelosLancamento
        .filter((existingModeloLancamento) => {
          const relatedDespesaReceitaIdFromExisting = existingModeloLancamento?.despesaReceita?.id;
          const relatedDespesaReceitaIdFromLocked = (
            state.locked.model.modeloLancamento?.despesaReceita?.id
          );

          return relatedDespesaReceitaIdFromExisting !== relatedDespesaReceitaIdFromLocked;
        })
        .some((existingModeloLancamento) => {
          const relatedDespesaReceitaIdFromExisting = existingModeloLancamento?.despesaReceita?.id;
          const relatedDespesaReceitaIdFromUpdated = updated?.despesaReceita?.id;

          return relatedDespesaReceitaIdFromExisting === relatedDespesaReceitaIdFromUpdated;
        });

      if (alreadyExists) {
        const tipoModeloLabelOfUpdatedModeloLancamento = getTipoModeloLabelByValue(
          updated?.tipo
        );

        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.mensagem.despesaReceitaExisteModeloNumerario', { tipoModeloLancamento: tipoModeloLabelOfUpdatedModeloLancamento, despesaReceita: updated?.despesaReceita?.id }),
        };
        return;
      }

      state.related.model.modeloLancamento = updated;
      state.model.modelosLancamento = state.model.modelosLancamento.map(
        (current) => ((current?.id === updated?.id)
          ? updated : current
        )
      );
      state.mode.sub = SUB_CONSULT_MODE;
      state.locked.model.modeloLancamento = {};
    },
    removeModeloLancamentoById: (state, action) => {
      const { id } = action.payload.data;
      state.model.modelosLancamento = state.model.modelosLancamento.filter((current) => (
        current?.id !== id
      ));
      state.related.model.modeloLancamento = initialState.related.model.modeloLancamento;
      state.mode.sub = NONE_SUB_MODE;
    },
    resetModelosLancamento: (state, action) => {
      state.model.modelosLancamento = action.payload.data;
      state.related.model.modeloLancamento = initialState.related.model.modeloLancamento;
      state.mode.sub = NONE_SUB_MODE;
    },
    resetRelatedModeloLancamentoModel: (state) => {
      state.related.model.modeloLancamento = {};

      if (!isSubCreateMode(state.mode.sub)) {
        state.mode.sub = NONE_SUB_MODE;
      }
    },
    setResponse: (state, action) => {
      state.response = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearResponse: (state) => {
      state.response = {
        status: null,
        message: null,
        data: null,
      };
    },
    clearError: (state) => {
      state.error = initialState.error;
    },
    resetModel: (state) => {
      state.model = initialState.model;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllAsync.pending, (state) => {
        state.status = ALTERNATIVE_LOADING_STATUS;
      })
      .addCase(fetchAllAsync.fulfilled, (state, action) => {
        state.status = IDLE_STATUS;
        state.list = action.payload.data;
      })
      .addCase(fetchAllAsync.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.erroCarregarListaModelosNumerario', { erro: action.error.message }),
        };
      })
      .addCase(fetchAllAsyncAndProcess.pending, (state) => {
        state.status = ALTERNATIVE_LOADING_STATUS;
      })
      .addCase(fetchAllAsyncAndProcess.fulfilled, (state, action) => {
        state.status = IDLE_STATUS;
        state.list = action.payload.data;
      })
      .addCase(fetchAllAsyncAndProcess.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.erroCarregarListaModelosNumerario', { erro: action.error.message }),
        };
      })
      .addCase(fetchByIdAsync.pending, (state) => {
        state.status = LOADING_STATUS;
      })
      .addCase(fetchByFilterAsync.pending, (state) => {
        state.status = LOADING_STATUS;
      })
      .addCase(fetchByIdAsync.fulfilled, (state, action) => {
        if (action.payload.response.status === 200) {
          state.model = {
            ...action.payload.response.data,
          };
          state.status = SUCCESS_STATUS;
        }
      })
      .addCase(fetchByFilterAsync.fulfilled, (state, action) => {
        if (action.payload.response.status === 200) {
          state.model = [action.payload.response.data];
          state.status = SUCCESS_STATUS;
        }
      })
      .addCase(fetchByFilterAsync.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.erroCarregarModeloNumerario', { erro: action.error.message }),
        };
      })
      .addCase(fetchByIdAsync.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.erroCarregarModeloNumerario', { erro: action.error.message }),
        };
      })
      .addCase(activateByIdAsync.pending, (state) => {
        state.status = LOADING_STATUS;
      })
      .addCase(activateByIdAsync.fulfilled, (state, action) => {
        state.status = SUCCESS_STATUS;
        state.response.status = action.payload.response.status;
        state.response.data = action.payload.response.data;

        const { description } = action.payload.response.data;
        state.response.message = i18n.t('com.muralis.qcx.mensagem.modeloNumerarioAtivado', { nome: description });

        if (state.response.status === 200) {
          state.list = state.list
            .map((current) => (
              current.id === action.payload.response.data.id
                ? action.payload.response.data
                : current
            ));
        }
      })
      .addCase(activateByIdAsync.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.erroAtivarModeloNumerario', { erro: action.error.message }),
        };
      })
      .addCase(inactivateByIdAsync.pending, (state) => {
        state.status = LOADING_STATUS;
      })
      .addCase(inactivateByIdAsync.fulfilled, (state, action) => {
        state.status = SUCCESS_STATUS;
        state.response.status = action.payload.response.status;
        state.response.data = action.payload.response.data;

        const { description } = action.payload.response.data;
        state.response.message = i18n.t('com.muralis.qcx.mensagem.modeloNumerarioInativado', { nome: description });

        if (state.response.status === 200) {
          state.list = state.list
            .map((current) => (
              current.id === action.payload.response.data.id
                ? action.payload.response.data
                : current
            ));
        }
      })
      .addCase(inactivateByIdAsync.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.erroInativarModeloNumerario', { erro: action.error.message }),
        };
      });
  },
});

const {
  changeStatusTo,
  loading,
  success,
  failure,
  resetStatus,
  resetList,
  changeToCreateMode,
  changeToConsultMode,
  changeToUpdateMode,
  resetMode,
  changeToBackgroundCreateMode,
  resetBackgroundMode,
  changeToSubCreateMode,
  changeToSubConsultMode,
  changeToSubUpdateMode,
  resetSubMode,
  changeControlTo,
  setModel,
  resetModel,
  addToList,
  updateOnList,
  setRelatedModeloLancamentoModel,
  addModeloLancamento,
  updateModeloLancamento,
  removeModeloLancamentoById,
  resetRelatedModeloLancamentoModel,
  lockSelectedModeloLancamento,
  resetLockedModeloLancamentoModel,
  setResponse,
  setResponseMessage,
  clearResponse,
  setError,
  clearError,
  resetModelosLancamento,
} = modeloNumerarioSlice.actions;

const selectModelosNumerario = (state) => state.modeloNumerario.list;
const selectStatus = (state) => state.modeloNumerario.status;
const selectMode = (state) => state.modeloNumerario.mode.main;
const selectBackgroundMode = (state) => state.modeloNumerario.mode.background;
const selectSubMode = (state) => state.modeloNumerario.mode.sub;
const selectConfigControl = (state) => state.modeloNumerario.config.control;
const selectError = (state) => state.modeloNumerario.error;
const selectResponse = (state) => state.modeloNumerario.response;
const selectModeloNumerario = (state) => state.modeloNumerario.model;
const selectRelatedModeloLancamentoModel = (state) => (
  state.modeloNumerario.related.model.modeloLancamento
);
const selectLockedModeloLancamentoModel = (state) => (
  state.modeloNumerario.locked.model.modeloLancamento
);

const modeloNumerarioActions = {
  ...modeloNumerarioSlice.actions,
  selectModelosNumerario,
  selectStatus,
  selectMode,
  selectBackgroundMode,
  selectSubMode,
  selectError,
  selectResponse,
  selectModeloNumerario,
  selectRelatedModeloLancamentoModel,
  selectLockedModeloLancamentoModel,
};

export {
  modeloNumerarioSlice,
  changeStatusTo,
  loading,
  success,
  failure,
  resetStatus,
  resetList,
  changeToCreateMode,
  changeToConsultMode,
  changeToUpdateMode,
  resetMode,
  changeToBackgroundCreateMode,
  resetBackgroundMode,
  changeToSubCreateMode,
  changeToSubConsultMode,
  changeToSubUpdateMode,
  resetSubMode,
  changeControlTo,
  setModel,
  resetModel,
  addToList,
  updateOnList,
  setRelatedModeloLancamentoModel,
  addModeloLancamento,
  updateModeloLancamento,
  removeModeloLancamentoById,
  resetRelatedModeloLancamentoModel,
  lockSelectedModeloLancamento,
  resetLockedModeloLancamentoModel,
  resetModelosLancamento,
  setResponse,
  setResponseMessage,
  clearResponse,
  setError,
  clearError,
  fetchAllAsync,
  fetchAllAsyncAndProcess,
  fetchByIdAsync,
  fetchByFilterAsync,
  activateByIdAsync,
  inactivateByIdAsync,
  selectModelosNumerario,
  selectModeloNumerario,
  selectRelatedModeloLancamentoModel,
  selectLockedModeloLancamentoModel,
  selectStatus,
  selectMode,
  selectBackgroundMode,
  selectSubMode,
  selectError,
  selectResponse,
  selectConfigControl,
  modeloNumerarioActions,
};

export default modeloNumerarioSlice.reducer;
